import * as B from '@blueprintjs/core';
import blueprintCss from '@blueprintjs/core/lib/css/blueprint.css?inline';
import blueprintIconsCss from '@blueprintjs/icons/lib/css/blueprint-icons.css?inline';
import * as I from 'immutable';
import leaflet from 'leaflet';
// Bundle the default marker icon PNGs as `?url` assets so Vite emits them with
// stable hashed paths instead of inlining `marker-icon.png` (which is <4KB) as
// a data URI. See the L.Icon.Default.mergeOptions call below for why this
// matters.
import leafletIconRetinaUrl from 'leaflet/dist/images/marker-icon-2x.png?url';
import leafletIconUrl from 'leaflet/dist/images/marker-icon.png?url';
import leafletShadowUrl from 'leaflet/dist/images/marker-shadow.png?url';
import leafletCss from 'leaflet/dist/leaflet.css?inline';
import leafletJsUrl from 'leaflet/dist/leaflet.js?url';
import React from 'react';

import {FONTS as ANALYZE_POLYGON_CHART_FONTS} from 'app/components/AnalyzePolygonChart/utils';
import GlobalErrorBoundary from 'app/components/ErrorBoundaries';
import api from 'app/modules/Remote/api';
import {ApiFeature, ApiNote, ApiReport, ReportState} from 'app/modules/Remote/Feature';
import {ApiFeatureCollection, GeometryOverlaySetting} from 'app/modules/Remote/FeatureCollection';
import {ApiOrganization, TagSetting} from 'app/modules/Remote/Organization/types';
import {useFeatureData} from 'app/providers/FeatureDataProvider';
import {GeoJsonFeaturesLoader} from 'app/providers/FeaturesProvider';
import {NotesActions, StateApiNoteWithTagSettings} from 'app/stores/NotesStore';
import * as hookUtils from 'app/utils/hookUtils';
import * as layerUtils from 'app/utils/layerUtils';
import {MultiFeaturePropertyParts} from 'app/utils/multiFeaturePropertyUtils';
import * as noteUtils from 'app/utils/noteUtils';
import * as tagUtils from 'app/utils/tagUtils';

import CSS_PRINT from './CssPrint';
import CSS_RESET from './CssReset';
import {useFontsReady, useReadyState} from './readyState';
import {ReportDocumentStructure} from './ReportDocumentStructure';
import {ReportExportFeatureWithNotes, ReportFeatureInfo} from './ReportExportFeature';

/**
 * Entry point for the entire contents of the ReportExportWindow. Handles
 * loading the feature data and notes across what may be multiple features.
 *
 * Delegates to ReportPrintPreview for the window contents.
 */
const ReportExportWindow: React.FunctionComponent<
  React.PropsWithChildren<{
    firebaseToken: string;
    organization: I.ImmutableOf<ApiOrganization>;
    geoJsonFeaturesLoader: GeoJsonFeaturesLoader;
    projectId: string;
    notesActions: Pick<NotesActions, 'hydrateNote'>;
    selectedFeatureCollection: I.ImmutableOf<ApiFeatureCollection>;
    selectedFeature: I.ImmutableOf<ApiFeature>;
    overlayFeatureCollections: I.ImmutableOf<ApiFeatureCollection[]>;
    overlaySettings: GeometryOverlaySetting[];
    multiFeaturePropertyParts: MultiFeaturePropertyParts;
    includeAllParts: boolean;
    notesFilterState: noteUtils.NotesFilterState;
    savedReportId?: number | undefined;
    reportTitle?: string | undefined;
    refetchReports?: () => Promise<void>;
    reportWindow: Window | null;
  }>
> = ({
  firebaseToken,
  organization,
  geoJsonFeaturesLoader,
  notesActions,
  projectId,
  selectedFeatureCollection,
  selectedFeature,
  overlayFeatureCollections,
  overlaySettings,
  multiFeaturePropertyParts,
  notesFilterState,
  includeAllParts,
  savedReportId,
  reportTitle = 'Report',
  refetchReports,
  reportWindow,
}) => {
  const {getFeatureData} = useFeatureData();
  // We have our own note cache here because there isn’t one higher-up. The note
  // store code was written prior to our data fetching hooks, and so isn’t as-is
  // built for getting more than one feature’s notes at a time.
  const [getNotes] = hookUtils.useCachedApiGet(
    async ([notesActions, fcId, projectId], featureId: number) => {
      const apiNotes = (await api.featureCollections.features(fcId).listNotes(projectId, featureId))
        .get('data')
        .toJS() as ApiNote[];

      const hydratedNotesWithTagSettings: StateApiNoteWithTagSettings[] = apiNotes.map((n) => {
        const tagSettings: Record<string, TagSetting> = tagUtils
          .getTagSettings(organization, tagUtils.TAG_KIND.NOTE)
          .toJS();

        return {
          ...notesActions.hydrateNote(n),
          // It's possible for a tagId on a note to no longer exist in tagSettings, so we
          // filter out any tagIds that aren't in tagSettings at this step
          tagSettings: n.tagIds
            ? Object.keys(n.tagIds)
                .filter((tagId) => tagSettings[tagId])
                .map((tagId) => tagSettings[tagId])
            : null,
        };
      });
      return hydratedNotesWithTagSettings;
    },
    [notesActions, selectedFeatureCollection.get('id'), projectId] as const
  );

  const enrolledLayerKeys =
    layerUtils.getEnrolledLayerKeysForFeatureCollection(selectedFeatureCollection);

  // If all parts are involved, don’t make one of them “active,” which would
  // highlight them on the overview map.
  if (includeAllParts) {
    multiFeaturePropertyParts = multiFeaturePropertyParts.map((p) => ({...p, isActive: false}));
  }

  //TODO(eva): i think we should get rid of the concept of "loadedCount". it's for populating the loading bar,
  //but it's unhelpful for single-location properties because as soon as loadedCount = totalCount the report
  //is loaded anyway. putting this as a TODO bc want to also unwind the list of lists created here bc of loadedCount.
  const [features, loadedCount] = React.useMemo<[ReportFeatureInfo[] | null, number]>(() => {
    if (includeAllParts) {
      let features: ReportFeatureInfo[] | null = [];
      let loadedCount = 0;

      for (const {feature} of multiFeaturePropertyParts) {
        const featureData = getFeatureData(feature.get('id')).value;
        const notes = getNotes(feature.get('id')).value;

        // Our contract with the rest of the component is that we either return
        // an array of all of the ReportFeatureInfos, or return null. We don’t
        // want to return a partial array, since we don’t want report pages to
        // pop in. And we don’t want ReportFeatureInfo to have optional fields
        // for features and notes, since we’d have to check them later.
        //
        // _BUT_ we don’t want to short-circuit out of this loop entirely even
        // if we know we’re not going to be returning any ReportFeatureInfos.
        //
        // The reason for that is that we assume that getFeatureData and
        // getNotes are backed by useCachedApiGet. We need to call them to kick
        // off the initial requests and also to keep the pagination going.
        //
        // If we instead short-circuited at this case and returned null, then
        // the features and their data would load serially, rather than in
        // parallel, which would be slower.
        if (!featureData || !notes) {
          features = null;
        } else {
          loadedCount++;

          // This will no-op if we’re not returning features because some
          // haven’t loaded, and that’s fine.
          features?.push({
            feature,
            featureData,
            notes,
          });
        }
      }

      return [features, loadedCount];
    } else {
      // This should already be in the featureData cache established in
      // MonitorProjectView’s data provider.
      const featureData = getFeatureData(selectedFeature.get('id')).value;

      const notes = getNotes(selectedFeature.get('id')).value;

      if (!featureData || !notes) {
        return [null, 0];
      } else {
        return [
          [
            {
              feature: selectedFeature,
              featureData,
              notes,
            },
          ],
          1,
        ];
      }
    }
  }, [selectedFeature, includeAllParts, getFeatureData, getNotes, multiFeaturePropertyParts]);

  // useCallback because changing this value will reset the note order back to
  // the date sort, removing any re-ordering from the user clicking the arrow
  // buttons.
  const filterNotesCallback = React.useCallback(
    (n: StateApiNoteWithTagSettings) => noteUtils.filterNotesCallback(n, 'user', notesFilterState),
    [notesFilterState]
  );

  return (
    <ReportExportWindowContents
      firebaseToken={firebaseToken}
      organization={organization}
      projectId={projectId}
      geoJsonFeaturesLoader={geoJsonFeaturesLoader}
      overlayFeatureCollections={overlayFeatureCollections}
      overlaySettings={overlaySettings}
      propertyParts={multiFeaturePropertyParts}
      filterNotesCallback={filterNotesCallback}
      featureInfos={features}
      totalCount={includeAllParts ? multiFeaturePropertyParts.length : 1}
      loadedCount={loadedCount}
      enrolledLayerKeys={enrolledLayerKeys}
      savedReportId={savedReportId}
      reportTitle={reportTitle}
      refetchReports={refetchReports}
      reportWindow={reportWindow}
      selectedFeature={selectedFeature}
    />
  );
};

/**
 * Error content to show if there’s an exception during rendering the report.
 *
 * In read-only mode it also emits #report-error, since the PDF export job waits on that
 * marker or #report-ready and would otherwise sit until its timeout.
 */
export const ReportError: React.FunctionComponent<{readOnly?: boolean}> = ({readOnly = false}) => {
  return (
    <>
      {readOnly && <div id="report-error" data-testid="report-error" aria-hidden />}
      <section>
        <h1>An error has occurred.</h1>
        <p>
          There was a problem generating your report. This might be a temporary hiccup, or it might
          mean our software has some bugs that we need to fix.
        </p>
        <p>
          You can close this window, reload Lens, and try again. If this keeps happening, please
          send us an email at <a href="mailto:lens@upstream.tech">lens@upstream.tech</a>.
        </p>
      </section>
    </>
  );
};

/**
 * Leaflet _heavily_ assumes that it’s running in the same window / document as
 * its DOM elements, even moreso than MapboxGL. So, rather than try to hack
 * through it, we add a Leaflet <script> tag to the page and expose its object
 * in this Context.
 */
export const LeafletContext = React.createContext<typeof leaflet | null>(null);

/**
 * Component that inserts <head> and <body> tags for report export.
 *
 * Includes Blueprint stylesheets and also brings in a <script> tag for Leaflet.
 * Does not include our general app custom .styl styles, but does both a CSS
 * reset and brings in CssPrint.ts’s styles.
 *
 * This component’s children will be rendered in the body of the window,
 * surrounded by a LeafletContext.
 *
 * NOTE! There are some fun time shenanigans here because all of our React code
 * is running in the _parent_ window. We have portal’d our DOM content into this
 * popup window. For some browsers, there are strict difference between the
 * classes of DOM elements running across different documents, which can cause
 * errors.
 *
 * It’s because of that that we have the Leaflet code inserted via <script>
 * element so that it runs in the popup window’s DOM. We’re able to pull the
 * Leaflet object out of that window’s global namespace and make it available to
 * our React code, however.
 *
 * This works for now! But is something that might be fragile if browsers change
 * any behavior around communicating across even same-domain windows.
 */
export const PrintWindowStructure: React.FunctionComponent<
  React.PropsWithChildren<{
    title: string;
    fontsToCheck?: string[];
    onFontsReady?: () => void;
    onFontsTimeout?: () => void;
  }>
> = ({title, fontsToCheck, onFontsReady, onFontsTimeout, children}) => {
  const headRef = React.useRef<HTMLHeadElement>(null);

  // Reference to the pop-up’s Leaflet instance.
  const [L, setL] = React.useState<typeof leaflet | null>(null);

  // Adds the Leaflet <script> tag to the <head> and captures the Leaflet
  // instance asynchronously.
  React.useLayoutEffect(() => {
    const headEl = headRef.current!;
    const popupDocument = headEl.ownerDocument!;
    const popupWindow = popupDocument.defaultView;

    const script = popupDocument.createElement('script');
    script.src = leafletJsUrl;
    script.async = true;
    // The default Leaflet script sets itself on window.L, so we can find it
    // there and then store it in our local state.
    script.onload = () => {
      const popupL = (popupWindow as typeof window).L;
      // Point L.Icon.Default at the bundled marker PNGs. Leaflet normally
      // auto-detects this path by reading the computed background-image of a
      // hidden `.leaflet-default-icon-path` div and stripping the trailing
      // "marker-icon.png" filename. That breaks for us in production: Vite
      // inlines marker-icon.png (under its 4KB asset-inline threshold) as a
      // base64 `data:` URI, so the regex finds no filename to strip and the
      // fallback `<link href$="leaflet.css">` lookup fails too because the
      // CSS is injected via <style>, not <link>. End result: imagePath is ""
      // and default markers render as broken `<img src="marker-icon-2x.png">`
      // tags. Setting the URLs explicitly bypasses the heuristic entirely.
      popupL.Icon.Default.mergeOptions({
        iconUrl: leafletIconUrl,
        iconRetinaUrl: leafletIconRetinaUrl,
        shadowUrl: leafletShadowUrl,
      });
      setL(popupL);
    };
    headEl.appendChild(script);
  }, []);

  useFontsReady(() => headRef.current!.ownerDocument!, fontsToCheck, onFontsReady, onFontsTimeout);

  return (
    <>
      <head ref={headRef}>
        <title>{title}</title>

        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1.0, user-scalable=no" />

        <style type="text/css">
          {leafletCss}
          {blueprintIconsCss}
          {blueprintCss}
          {CSS_RESET}
          {CSS_PRINT}
        </style>
      </head>

      <body>
        <LeafletContext.Provider value={L}>{children}</LeafletContext.Provider>
      </body>
    </>
  );
};

/**
 * HTML structure for the report export window, given loaded feature information
 * (FeatureData and notes).
 */
export const ReportExportWindowContents: React.FunctionComponent<
  React.PropsWithChildren<{
    firebaseToken: string;
    organization: I.ImmutableOf<ApiOrganization>;
    projectId: string;
    geoJsonFeaturesLoader: GeoJsonFeaturesLoader;
    filterNotesCallback: (note: StateApiNoteWithTagSettings) => boolean;
    overlayFeatureCollections: I.ImmutableOf<ApiFeatureCollection[]>;
    overlaySettings: GeometryOverlaySetting[];
    featureInfos: ReportFeatureInfo[] | null;
    propertyParts: MultiFeaturePropertyParts;
    totalCount: number;
    loadedCount: number;
    enrolledLayerKeys: string[];
    savedReportId?: number | undefined;
    /**
     * The already-loaded report, for callers that fetched it themselves. Without it the
     * report tree fetches savedReportId through the logged-in reports API.
     */
    savedReport?: ApiReport<ReportState>;
    reportTitle: string;
    refetchReports?: () => Promise<void>;
    reportWindow: Window | null;
    selectedFeature: I.ImmutableOf<ApiFeature>;
    /**
     * 'document' renders into the current page instead of a popup window. Public report
     * pages use it; the logged-in popup keeps the default.
     */
    structure?: 'window' | 'document';
    /**
     * Strips every way to edit or save the report. Load-bearing beyond appearance: the
     * editing toolbar calls useUserInfo, which throws when nobody is logged in. Any hook
     * added to this tree that assumes a logged-in session breaks the public page the same
     * way.
     */
    readOnly?: boolean;
  }>
> = ({
  firebaseToken,
  organization,
  projectId,
  geoJsonFeaturesLoader,
  overlayFeatureCollections,
  overlaySettings,
  filterNotesCallback,
  featureInfos,
  totalCount,
  loadedCount,
  propertyParts,
  enrolledLayerKeys,
  savedReportId,
  savedReport,
  reportTitle,
  refetchReports,
  reportWindow,
  selectedFeature,
  structure = 'window',
  readOnly = false,
}) => {
  const [areMapsReady, areMapsReadyReporter] = useReadyState(false);
  const [areFontsReady, setAreFontsReady] = React.useState(false);

  const Structure = structure === 'document' ? ReportDocumentStructure : PrintWindowStructure;

  return (
    <Structure
      title={featureInfos === null ? 'Loading...' : reportTitle}
      fontsToCheck={ANALYZE_POLYGON_CHART_FONTS}
      onFontsReady={() => {
        setAreFontsReady(true);
      }}
      /** If we timed out waiting for our fonts to load, we still want to
       * proceed with rendering components that depend on them. We handle this
       * somewhat gracefully by setting CSS font fallbacks. */
      onFontsTimeout={() => {
        setAreFontsReady(true);
      }}
    >
      {/*
          If there’s an error in report generation we want to catch it here, since otherwise it
          would bubble up and crash the main Lens window.

          We put the error boundary inside PrintWindowContents so we get the CSS, though.
      */}
      <GlobalErrorBoundary errorContent={<ReportError readOnly={readOnly} />}>
        {!featureInfos && (
          <div style={{width: '30%', margin: '10rem auto'}}>
            <B.ProgressBar value={loadedCount / totalCount} intent="primary" />
          </div>
        )}

        {featureInfos && (
          <>
            <ReportExportFeatureWithNotes
              firebaseToken={firebaseToken}
              organization={organization}
              projectId={projectId}
              geoJsonFeaturesLoader={geoJsonFeaturesLoader}
              overlayFeatureCollections={overlayFeatureCollections}
              overlaySettings={overlaySettings}
              filterNotesCallback={filterNotesCallback}
              propertyParts={propertyParts}
              isMultiPartProperty={propertyParts.length > 1}
              isCombinedMultiPart={featureInfos.length > 1}
              areMapsReadyReporter={areMapsReadyReporter}
              areFontsReady={areFontsReady}
              enrolledLayerKeys={enrolledLayerKeys}
              areMapsReady={areMapsReady}
              savedReportId={savedReportId}
              savedReport={savedReport}
              featureInfos={featureInfos}
              reportTitle={reportTitle}
              refetchReports={refetchReports}
              reportWindow={reportWindow}
              selectedFeature={selectedFeature}
              readOnly={readOnly}
            />
          </>
        )}
      </GlobalErrorBoundary>
    </Structure>
  );
};

export default ReportExportWindow;
